import React, { useCallback, useEffect, useState } from 'react';
import MainTable from '@Components/MainTable';
import { fetchAllInstructors } from '@Pages/Instructors/Slices/instructorsSlice';
import { usePageLoader } from '@Providers/LoadingProvider';
import { useTypedSelector } from '@Features/store';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NoResults from '@Components/NoResults/NoResults';
import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import Avatar from '@mui/material/Avatar';
import TableCell from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import Tooltip from '@mui/material/Tooltip';
import { TableHeader } from '@Features/generics/generics.type';
import { useSnackbar } from '@Providers/useSnackbar';
import ActivationModel from './components/ActiviationModel/ActivationModel';
import SidebarContentWrapper from '@Components/SidebarContentWrapper';
import useFilters from '@Hooks/useFilters';
import useQueryURL from '@Hooks/useQueryUrl';
import {
	activateInstructor,
	deactivateInstructor,
} from '@Services/instructors/instructors.req';
import { TableCellStyled } from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/EnrollmentStudent';
import { StyledAvatarGrouped } from '@Pages/ClassRooms/ClassRoomsTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { TooltipTitle } from '@Utils/TooltipTitle';
import educationBadge, { educationFullNames } from '@Utils/educationBadge';
import AddInstructorButton from './components/AddInstructorBtn';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import EdActiveStatus from '@Components/UI/Utilities/EdActiveStatus/EdActiveStatus';

const TABLE_HEADS: Array<TableHeader> = [
	// One Name column instead of separate first/last. Sorts on first_name —
	// the backend orders by a single column.
	{ displayName: 'NAME', fieldName: 'first_name', canSort: true },
	{ displayName: 'LABEL', fieldName: 'label' },
	{ displayName: 'YEAR', fieldName: 'branch_id', canSort: false },
	{ displayName: 'SECTION', fieldName: 'branch_name', canSort: false },
	{ displayName: 'WEIGHT', fieldName: 'weight' },
	{ displayName: 'ACTIVE', fieldName: 'active', canSort: false },
	{ displayName: 'CREATED AT', fieldName: 'created_at' },
	{ displayName: 'STATUS', fieldName: 'active', canSort: false },
];

/** Either half can be missing, so join on what is there rather than printing a
 *  stray space or the word "undefined". */
const fullName = (user?: { first_name?: string; last_name?: string }) =>
	[user?.first_name, user?.last_name].filter(Boolean).join(' ') || '-';

interface Props {}

const InstructorsTable: React.FC<Props> = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { displaySnackbar } = useSnackbar();
	const { setIsLoading } = usePageLoader();
	const { instructors, lastPage, loaders, filters, total, perPage, page } =
		useTypedSelector((state) => state.instructor);
	const auth = useTypedSelector((state) => state.auth);
	const hasActivePermission =
		auth.user.type === 'super' || auth.permissions.has('act-inst');
	const hasDeActivePermission =
		auth.user.type === 'super' || auth.permissions.has('dact-inst');

	const [activate, setActivateInsructor] = useState<boolean>(false);
	const [instructorId, setInsrtuctorId] = useState<any>(null);
	const [openModel, setOpenModel] = useState(false);
	const { selectedFilters } = useFilters();
	const { selectedQuery } = useQueryURL();

	const handleModelOpen = (id: number, active?: boolean) => {
		setOpenModel(true);
		setActivateInsructor(active ?? false);
		setInsrtuctorId(id);
	};

	const handleModelClose = () => {
		setOpenModel(false);
		setActivateInsructor(false);
	};

	const onActivateInstructor = useCallback(() => {
		setIsLoading(true);
		if (!activate) {
			activateInstructor(instructorId)
				.then(() => {
					displaySnackbar('success', 'Activated Instructor successfully');
					dispatch(
						fetchAllInstructors({
							page,
							perPage,
							sortBy: '',
							filters: selectedFilters,
							query: selectedQuery,
						})
					);
				})
				.catch((e: any) => {
					const msg =
						e.response?.data.message || 'Unable to Activated Instructor ';
					displaySnackbar('error', msg);
				});
		} else {
			deactivateInstructor(instructorId)
				.then(() => {
					displaySnackbar('success', 'Deactivated Instructor successfully');
					dispatch(
						fetchAllInstructors({
							page,
							perPage,
							sortBy: '',
							filters: selectedFilters,
							query: selectedQuery,
						})
					);
				})
				.catch((e: any) => {
					const msg =
						e.response?.data.message || 'Unable to Deactivated Instructor ';
					displaySnackbar('error', msg);
				});
		}
		setOpenModel(false);
		setIsLoading(false);
	}, [activate, setIsLoading, dispatch, instructorId, displaySnackbar]);

	useEffect(() => {
		setIsLoading(!!loaders.fetchAll);
	}, [setIsLoading, loaders.fetchAll]);

	const handlePagination = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
		}: onPageChangeRequestMeta) => {
			dispatch(
				fetchAllInstructors({
					page: activePage,
					perPage: perPage ?? 10,
					sortBy,
					filters,
					query,
					from: startDate,
					to: endDate,
				})
			);
		},
		[]
	);

	// Cell clicks and the row click both navigate, and the cell handler fires
	// first. This used to push `/dashboard/instructors/update/:uuid` — a route
	// that does not exist — so every cell click buried a dead entry under the
	// edit page and Back landed on a blank content area. Point it at the same
	// URL the row uses.
	const handleRowClick = async (uuid?: string) => {
		if (!uuid) return;
		history.push(`/dashboard/instructors/edit/instructor/${uuid}/info`);
	};

	return (
		<SidebarContentWrapper>
			{(auth.user.type === 'super' || auth.permissions.has('c-inst')) && (
				<div className="role_head__container">
					<AddInstructorButton />
				</div>
			)}
			<ActivationModel
				text="Instructor"
				openModel={openModel}
				handleModelClose={handleModelClose}
				ban={activate}
				onConfirmation={onActivateInstructor}
			/>

			<MainTable
				onPageChangeRequest={handlePagination}
				totalPages={lastPage}
				tableFilters={filters}
				total={Number(total)}
				RowsperPage={Number(perPage)}
				pageNum={Number(page)}
				tableHeads={TABLE_HEADS}
				renderItems={() => {
					if (instructors.length === 0) {
						return (
							<MainTableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					} else {
						return instructors.map((_instructor) => (
							<MainTableRow
								key={_instructor.uuid}
								hover
								sx={{
									cursor: 'pointer',
									'&:active': { backgroundColor: '#6c63ff' },
								}}
								onClick={() => {
									history.push({
										pathname: `/dashboard/instructors/edit/instructor/${_instructor.uuid}/info`,
									});
								}}
							>
								<Tooltip title={fullName(_instructor)} placement="top">
									<TableCellStyled
										onClick={() => handleRowClick(_instructor?.uuid)}
									>
										{fullName(_instructor)}
									</TableCellStyled>
								</Tooltip>
								<Tooltip title={_instructor?.instructor?.label} placement="top">
									<TableCellStyled
										onClick={() => handleRowClick(_instructor.uuid)}
									>
										{_instructor?.instructor?.label || '-'}
									</TableCellStyled>
								</Tooltip>
								<TableCellStyled
									onClick={() => handleRowClick(_instructor.uuid)}
								>
									<Tooltip
										title={TooltipTitle(
											educationFullNames(_instructor?.instructor?.educationYears)
										)}
										placement="top"
									>
										<StyledAvatarGrouped max={2}>
											{(_instructor?.instructor?.educationYears ?? []).map(
												(edu: any) => {
													const badge = educationBadge(edu);
													return (
														<Avatar
															alt="education year"
															key={edu.id}
															sx={{
																backgroundColor: badge.color,
																fontFamily: 'Roboto',
															}}
														>
															{badge.short}
														</Avatar>
													);
												}
											)}
										</StyledAvatarGrouped>
									</Tooltip>
								</TableCellStyled>

								<TableCellStyled
									onClick={() => handleRowClick(_instructor.uuid)}
								>
									<Tooltip
										title={TooltipTitle(
											educationFullNames(
												_instructor?.instructor?.educationSections
											)
										)}
										placement="top"
									>
										<StyledAvatarGrouped max={2}>
											{(_instructor?.instructor?.educationSections ?? []).map(
												(edu: any) => {
													const badge = educationBadge(edu);
													return (
														<Avatar
															alt="education section"
															key={edu.id}
															sx={{
																backgroundColor: badge.color,
																fontFamily: 'Roboto',
															}}
														>
															{badge.short}
														</Avatar>
													);
												}
											)}
										</StyledAvatarGrouped>
									</Tooltip>
								</TableCellStyled>

								<TableCellStyled
									onClick={() => handleRowClick(_instructor.uuid)}
								>
									{_instructor?.instructor?.weight || '-'}
								</TableCellStyled>
								<TableCell
									sx={{
										padding: '0 1.063rem 0',
										whiteSpace: 'nowrap',
									}}
									onClick={() => handleRowClick(_instructor.uuid)}
								>
									<EdActiveStatus active={_instructor.instructor.is_active} />
								</TableCell>
								<Tooltip
									title={<DateTimeFormatter date={_instructor?.created_at} />}
									placement="top"
								>
									<TableCell
										sx={{
											fontFamily: 'Roboto',
											fontSize: '1rem',
											padding: '0 1.063rem 0',
										}}
										onClick={() => handleRowClick(_instructor.uuid)}
									>
										{_instructor.created_at ? (
											<DateTimeFormatter date={_instructor?.created_at} />
										) : (
											'-'
										)}
									</TableCell>
								</Tooltip>
								<TableCell
									sx={{
										fontFamily: 'Roboto',
										fontSize: '1rem',
										padding: '0 1.063rem 0',
									}}
								>
									<Tooltip
										placement="left"
										title={
											_instructor?.instructor?.is_active
												? 'Deactivate Instructor'
												: 'Activate Instructor'
										}
									>
										<Button
											id="instructoBanUnBan"
											disabled={
												(!hasActivePermission &&
													!_instructor?.instructor?.is_active) ||
												(!hasDeActivePermission &&
													_instructor?.instructor.is_active)
											}
											onClick={(e) => {
												e.stopPropagation();
												handleModelOpen(
													_instructor?.id,
													_instructor?.instructor?.is_active
												);
											}}
										>
											{!_instructor?.instructor?.is_active ? (
												<AccountBoxIcon
													sx={{
														color: '#f74343',
														height: '26px',
														width: '26px',
													}}
												/>
											) : (
												<AccountBoxIcon
													sx={{
														color: '#5ac0fc',
														height: '26px',
														width: '26px',
													}}
												/>
											)}
										</Button>
									</Tooltip>
								</TableCell>
							</MainTableRow>
						));
					}
				}}
			/>
		</SidebarContentWrapper>
	);
};

export default InstructorsTable;
